import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';

const PermissionsTab = () => {
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [permissionsSchema, setPermissionsSchema] = useState({});

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [expandedModules, setExpandedModules] = useState({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Template modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState('create'); // 'create' | 'edit'
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [showTemplateList, setShowTemplateList] = useState(false);

  // Pobierz dane inicjalne
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [locationsRes, templatesRes, schemaRes] = await Promise.all([
        api.get('/locations/'),
        api.get('/permissions/templates'),
        api.get('/permissions/schema')
      ]);

      setLocations(locationsRes.data.data || []);
      setTemplates(templatesRes.data.data || []);
      setPermissionsSchema(schemaRes.data.data || {});
    } catch (err) {
      console.error('Błąd pobierania danych:', err);
      setError('Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const refreshTemplates = async () => {
    try {
      const { data } = await api.get('/permissions/templates');
      setTemplates(data.data || []);
    } catch (err) {
      console.error('Błąd odświeżania szablonów:', err);
    }
  };

  const fetchLocationUsers = useCallback(async (locationId) => {
    if (!locationId) {
      setUsers([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.get(`/locations/${locationId}/employees`);
      setUsers(data.data || []);
    } catch (err) {
      console.error('Błąd pobierania użytkowników:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchLocationUsers(selectedLocation);
      setSelectedUser(null);
      setUserPermissions({});
    }
  }, [selectedLocation, fetchLocationUsers]);

  const selectUser = async (user) => {
    setUserPermissions({});
    setSelectedUser(user);
    setExpandedModules({});

    try {
      const { data } = await api.get(`/permissions/user/${user.user_login}/location/${selectedLocation}`);
      setUserPermissions(data.data?.uprawnienia || {});
    } catch (err) {
      console.error('Błąd:', err);
      setUserPermissions({});
    }
  };

  const togglePermission = (module, permission) => {
    setUserPermissions(prev => {
      const newPermissions = { ...prev };
      if (!newPermissions[module]) newPermissions[module] = {};

      newPermissions[module] = {
        ...newPermissions[module],
        [permission]: !newPermissions[module]?.[permission]
      };

      // Wyłącz dostęp = wyłącz wszystko
      if (permission === 'dostep' && !newPermissions[module][permission]) {
        Object.keys(newPermissions[module]).forEach(key => {
          newPermissions[module][key] = false;
        });
      }
      // Włącz cokolwiek = włącz dostęp
      if (permission !== 'dostep' && newPermissions[module][permission]) {
        newPermissions[module].dostep = true;
      }

      return newPermissions;
    });
  };

  const setModuleLevel = (module, level) => {
    // level: 'full' | 'view' | 'none'
    setUserPermissions(prev => {
      const newPermissions = { ...prev };
      const modulePerms = permissionsSchema[module]?.permissions || {};

      newPermissions[module] = {};

      if (level === 'full') {
        Object.keys(modulePerms).forEach(p => {
          newPermissions[module][p] = true;
        });
      } else if (level === 'view') {
        newPermissions[module].dostep = true;
        newPermissions[module].podglad = true;
      } else {
        Object.keys(modulePerms).forEach(p => {
          newPermissions[module][p] = false;
        });
      }

      return newPermissions;
    });
  };

  const applyTemplate = async (templateName) => {
    if (!selectedUser || !selectedLocation) return;
    try {
      setSaving(true);
      await api.post(
        `/permissions/user/${selectedUser.user_login}/location/${selectedLocation}/apply-template`,
        { template: templateName }
      );

      const { data: refreshData } = await api.get(
        `/permissions/user/${selectedUser.user_login}/location/${selectedLocation}`
      );
      setUserPermissions(refreshData.data?.uprawnienia || {});
      setSuccessMessage(`Zastosowano: ${templateName}`);
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Błąd aplikowania szablonu');
    } finally {
      setSaving(false);
    }
  };

  const savePermissions = async () => {
    if (!selectedUser || !selectedLocation) return;
    try {
      setSaving(true);
      await api.put(
        `/permissions/user/${selectedUser.user_login}/location/${selectedLocation}`,
        { permissions: userPermissions }
      );
      setSuccessMessage('Zapisano!');
      setTimeout(() => setSuccessMessage(''), 2000);
      fetchLocationUsers(selectedLocation);
    } catch (err) {
      setError('Błąd zapisywania');
    } finally {
      setSaving(false);
    }
  };

  // Template CRUD
  const openSaveAsTemplate = () => {
    if (!selectedUser || Object.keys(userPermissions).length === 0) {
      setError('Najpierw skonfiguruj uprawnienia użytkownika');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setTemplateModalMode('create');
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setShowTemplateModal(true);
  };

  const openEditTemplate = (template) => {
    setTemplateModalMode('edit');
    setEditingTemplate(template);
    setTemplateName(template.nazwa);
    setTemplateDescription(template.opis || '');
    // Load template permissions into the editor
    setUserPermissions(template.permissions || {});
    setShowTemplateModal(false); // modal otworzymy dopiero przy zapisie
  };

  const cancelEditTemplate = () => {
    setEditingTemplate(null);
    setTemplateModalMode('create');
    // Przywróć uprawnienia użytkownika jeśli był wybrany
    if (selectedUser && selectedLocation) {
      api.get(`/permissions/user/${selectedUser.user_login}/location/${selectedLocation}`)
        .then(({ data }) => setUserPermissions(data.data?.uprawnienia || {}))
        .catch(() => setUserPermissions({}));
    } else {
      setUserPermissions({});
    }
  };

  const saveEditedTemplate = () => {
    // Otwórz modal z nazwą/opisem do potwierdzenia
    setShowTemplateModal(true);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      setError('Nazwa szablonu jest wymagana');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setSaving(true);
      if (templateModalMode === 'create') {
        await api.post('/permissions/templates', {
          nazwa: templateName.trim(),
          opis: templateDescription.trim(),
          permissions: userPermissions
        });
        setSuccessMessage(`Szablon "${templateName}" utworzony!`);
      } else {
        await api.put(`/permissions/templates/${editingTemplate.id}`, {
          nazwa: templateName.trim(),
          opis: templateDescription.trim(),
          permissions: userPermissions
        });
        setSuccessMessage(`Szablon "${templateName}" zaktualizowany!`);
      }
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowTemplateModal(false);
      setEditingTemplate(null);
      await refreshTemplates();
    } catch (err) {
      console.error('Błąd zapisywania szablonu:', err);
      setError('Błąd zapisywania szablonu');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Usunąć szablon "${template.nazwa}"?`)) return;
    try {
      await api.delete(`/permissions/templates/${template.id}`);
      setSuccessMessage(`Szablon "${template.nazwa}" usunięty`);
      setTimeout(() => setSuccessMessage(''), 3000);
      await refreshTemplates();
    } catch (err) {
      console.error('Błąd usuwania szablonu:', err);
      setError(err.response?.data?.message || 'Błąd usuwania szablonu');
      setTimeout(() => setError(null), 3000);
    }
  };

  const countTemplatePermissions = (permissions) => {
    if (!permissions || typeof permissions !== 'object') return 0;
    let count = 0;
    Object.values(permissions).forEach(mod => {
      if (mod && typeof mod === 'object') {
        count += Object.values(mod).filter(v => v === true).length;
      }
    });
    return count;
  };

  const toggleExpand = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  // Ikony i kolory modułów
  const moduleConfig = {
    pos: { icon: '💳', color: 'var(--primary)', name: 'Kasa / POS' },
    produkty: { icon: '📦', color: 'var(--success)', name: 'Produkty' },
    magazyn: { icon: '🏭', color: 'var(--purple)', name: 'Magazyn' },
    faktury: { icon: '📄', color: 'var(--info)', name: 'Faktury' },
    klienci: { icon: '👥', color: 'var(--orange)', name: 'Klienci' },
    raporty: { icon: '📊', color: 'var(--success)', name: 'Raporty' },
    kasa_bank: { icon: '💰', color: 'var(--warning)', name: 'Kasa/Bank' },
    zamowienia: { icon: '🛒', color: 'var(--danger)', name: 'Zamówienia' },
    wysylka: { icon: '🚚', color: 'var(--orange)', name: 'Wysyłka' },
    admin: { icon: '⚙️', color: 'var(--danger)', name: 'Administracja' },
    ksiegowosc: { icon: '🧮', color: 'var(--info)', name: 'Księgowość' },
    cenowki: { icon: '🏷️', color: 'var(--success)', name: 'Ceny / Cenówki' },
    kupony: { icon: '🎟️', color: 'var(--purple)', name: 'Kupony / Rabaty' },
    wiadomosci: { icon: '✉️', color: 'var(--cyan)', name: 'Wiadomości' },
    kolektor: { icon: '📱', color: 'var(--text-secondary)', name: 'Kolektor danych' },
    faktury_zakupu: { icon: '🧾', color: 'var(--purple)', name: 'Faktury zakupu' }
  };

  const getModuleStatus = (moduleKey) => {
    const perms = userPermissions[moduleKey] || {};
    const schema = permissionsSchema[moduleKey]?.permissions || {};
    const total = Object.keys(schema).length;
    const enabled = Object.keys(schema).filter(p => perms[p]).length;

    if (enabled === 0) return { label: 'Brak', color: 'var(--danger)', bg: 'var(--danger-light)' };
    if (enabled === total) return { label: 'Pełny', color: 'var(--success)', bg: 'var(--success-light)' };
    if (perms.dostep && perms.podglad && enabled <= 2) return { label: 'Podgląd', color: 'var(--info)', bg: 'var(--info-light)' };
    return { label: `${enabled}/${total}`, color: 'var(--orange)', bg: 'var(--warning-light)' };
  };

  // Style
  const styles = {
    container: { padding: '0.75rem', fontSize: '12px' },
    header: {
      border: '1px solid var(--primary)', color: 'var(--primary)' , padding: '0.5rem 1rem',
      borderRadius: '6px', marginBottom: '0.75rem', display: 'flex',
      justifyContent: 'space-between', alignItems: 'center'
    },
    card: {
      backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
      borderRadius: '6px', marginBottom: '0.5rem', overflow: 'hidden'
    },
    select: {
      padding: '6px 10px', border: '1px solid var(--border)',
      borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)',
      fontSize: '12px', minWidth: '180px'
    },
    btn: {
      padding: '4px 10px',
      cursor: 'pointer', fontSize: '11px', fontWeight: '500'
    },
    toggle: (active) => ({
      width: '36px', height: '18px', borderRadius: '9px',
      backgroundColor: active ? 'var(--success)' : 'var(--border)',
      position: 'relative', cursor: 'pointer', transition: 'all 0.2s',
      display: 'inline-block', flexShrink: 0
    }),
    toggleKnob: (active) => ({
      width: '14px', height: '14px', borderRadius: '50%',
      backgroundColor: 'var(--card-bg)', position: 'absolute', top: '2px',
      left: active ? '20px' : '2px', transition: 'all 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
    })
  };

  const Toggle = ({ checked, onChange }) => (
    <div style={styles.toggle(checked)} onClick={onChange}>
      <div style={styles.toggleKnob(checked)} />
    </div>
  );

  // Template modal
  const TemplateModal = () => {
    if (!showTemplateModal) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 2000
      }}>
        <div style={{
          backgroundColor: 'var(--card-bg)', borderRadius: '8px',
          width: '400px', maxWidth: '90vw', overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
        }}>
          {/* Modal header */}
          <div style={{
            padding: '0.75rem 1rem',
            background: 'linear-gradient(135deg, var(--primary), var(--info))',
            color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontWeight: '600', fontSize: '13px' }}>
              {templateModalMode === 'create' ? '📋 Zapisz jako szablon' : '✏️ Edytuj szablon'}
            </span>
            <button onClick={() => setShowTemplateModal(false)}
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer' }}>
              ×
            </button>
          </div>
          {/* Modal body */}
          <div style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', marginBottom: '0.3rem', color: 'var(--text-secondary)' }}>
                Nazwa szablonu *
              </label>
              <input
                type="text"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                placeholder="np. Kasjer, Kierownik..."
                autoFocus
                style={{
                  width: '100%', padding: '0.5rem', fontSize: '12px',
                  border: '1px solid var(--border)', borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
                onKeyDown={e => { if (e.key === 'Enter') saveTemplate(); }}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', marginBottom: '0.3rem', color: 'var(--text-secondary)' }}>
                Opis (opcjonalny)
              </label>
              <input
                type="text"
                value={templateDescription}
                onChange={e => setTemplateDescription(e.target.value)}
                placeholder="Krótki opis uprawnień..."
                style={{
                  width: '100%', padding: '0.5rem', fontSize: '12px',
                  border: '1px solid var(--border)', borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{
              padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px',
              fontSize: '10px', color: 'var(--text-muted)', marginBottom: '0.75rem'
            }}>
              {templateModalMode === 'create'
                ? `Zostanie zapisany aktualny zestaw uprawnień (${countTemplatePermissions(userPermissions)} włączonych)`
                : `Szablon zostanie zaktualizowany z bieżącymi ustawieniami (${countTemplatePermissions(userPermissions)} włączonych)`
              }
            </div>
          </div>
          {/* Modal footer */}
          <div style={{
            padding: '0.75rem 1rem', borderTop: '1px solid var(--border)',
            display: 'flex', justifyContent: 'flex-end', gap: '0.5rem'
          }}>
            <button
              onClick={() => setShowTemplateModal(false)}
              style={{ ...styles.btn, border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)' }}
            >
              Anuluj
            </button>
            <button
              onClick={saveTemplate}
              disabled={saving || !templateName.trim()}
              style={{
                ...styles.btn,
                backgroundColor: (!saving && templateName.trim()) ? 'var(--primary)' : 'var(--border)',
                color: '#fff'
              }}
            >
              {saving ? '⏳ Zapisywanie...' : templateModalMode === 'create' ? '💾 Zapisz szablon' : '💾 Aktualizuj'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Template Modal */}
      <TemplateModal />

      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '16px' }}>🔐</span>
          <span style={{ fontWeight: '600', fontSize: '13px' }}>Uprawnienia użytkowników</span>
        </div>
        <button
          onClick={() => setShowTemplateList(!showTemplateList)}
          style={{
            ...styles.btn,
            backgroundColor: showTemplateList ? 'var(--primary)' : 'var(--bg)',
            color: showTemplateList ? '#fff' : 'var(--text-secondary)',
            border: '1px solid var(--border)',
            fontSize: '11px'
          }}
        >
          📋 Szablony ({templates.length})
        </button>
      </div>

      {/* Komunikaty */}
      {error && (
        <div style={{ padding: '0.5rem', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px', marginBottom: '0.5rem', fontSize: '11px' }}>
          {error} <span style={{ float: 'right', cursor: 'pointer' }} onClick={() => setError(null)}>✕</span>
        </div>
      )}
      {successMessage && (
        <div style={{ padding: '0.5rem', backgroundColor: 'var(--success-light)', color: 'var(--success)', borderRadius: '4px', marginBottom: '0.5rem', fontSize: '11px' }}>
          ✓ {successMessage}
        </div>
      )}

      {/* Template list panel */}
      {showTemplateList && (
        <div style={{
          ...styles.card,
          marginBottom: '0.75rem',
          border: '1px solid var(--primary)',
        }}>
          <div style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontWeight: '600', fontSize: '12px', color: 'var(--primary)' }}>
              📋 Szablony uprawnień
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              {templates.length} {templates.length === 1 ? 'szablon' : templates.length < 5 ? 'szablony' : 'szablonów'}
            </span>
          </div>
          <div style={{ padding: '0.5rem' }}>
            {templates.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px' }}>
                Brak szablonów. Skonfiguruj uprawnienia użytkownika i kliknij "Zapisz jako szablon".
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
                {templates.map(t => (
                  <div key={t.id} style={{
                    padding: '0.6rem',
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '11px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.3rem' }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '12px' }}>
                          {t.is_default ? '⭐ ' : ''}{t.nazwa}
                        </div>
                        {t.opis && (
                          <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                            {t.opis}
                          </div>
                        )}
                      </div>
                      <span style={{
                        padding: '0.1rem 0.3rem', backgroundColor: 'var(--info-light)',
                        color: 'var(--info)', borderRadius: '3px', fontSize: '9px', fontWeight: '600', whiteSpace: 'nowrap'
                      }}>
                        {countTemplatePermissions(t.permissions)} uprawn.
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.4rem' }}>
                      {selectedUser && selectedLocation && (
                        <button
                          onClick={() => applyTemplate(t.nazwa)}
                          style={{
                            ...styles.btn, flex: 1,
                            backgroundColor: 'var(--success)', color: '#fff'
                          }}
                        >
                          ✓ Zastosuj
                        </button>
                      )}
                      <button
                        onClick={() => openEditTemplate(t)}
                        style={{
                          ...styles.btn, flex: 1,
                          backgroundColor: 'var(--info)', color: '#fff'
                        }}
                      >
                        ✏️ Edytuj
                      </button>
                      {!t.is_default && (
                        <button
                          onClick={() => deleteTemplate(t)}
                          style={{
                            ...styles.btn,
                            backgroundColor: 'var(--danger)', color: '#fff'
                          }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wybór lokalizacji i użytkownika - w jednej linii */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>📍 Lokalizacja:</span>
          <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} style={styles.select}>
            <option value="">-- wybierz --</option>
            {locations.map(loc => (
              <option key={loc.id} value={loc.id}>{loc.nazwa}</option>
            ))}
          </select>
        </div>

        {selectedLocation && users.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>👤 Użytkownik:</span>
            <select
              value={selectedUser?.user_login || ''}
              onChange={(e) => {
                const user = users.find(u => u.user_login === e.target.value);
                if (user) selectUser(user);
              }}
              style={styles.select}
            >
              <option value="">-- wybierz --</option>
              {users.map(u => (
                <option key={u.user_login} value={u.user_login}>
                  {u.user_login} ({u.rola})
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedUser && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>📋 Szablon:</span>
              <select onChange={(e) => e.target.value && applyTemplate(e.target.value)} style={styles.select} defaultValue="">
                <option value="">-- zastosuj --</option>
                {templates.map(t => (
                  <option key={t.nazwa} value={t.nazwa}>{t.nazwa}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '0.3rem', marginLeft: 'auto' }}>
              <button
                onClick={openSaveAsTemplate}
                style={{
                  ...styles.btn,
                  backgroundColor: 'var(--info)',
                  color: '#fff'
                }}
              >
                📋 Zapisz jako szablon
              </button>
              <button
                onClick={savePermissions}
                disabled={saving}
                style={{ ...styles.btn, backgroundColor: saving ? 'var(--text-muted)' : 'var(--success)', color: '#fff' }}
              >
                {saving ? '⏳' : '💾'} Zapisz
              </button>
            </div>
          </>
        )}
      </div>

      {/* Banner edycji szablonu */}
      {editingTemplate && (
        <div style={{
          padding: '0.6rem 1rem', marginBottom: '0.5rem',
          backgroundColor: 'var(--info-light)', border: '2px solid var(--info)',
          borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--info)' }}>
            <strong>✏️ Edycja szablonu: {editingTemplate.nazwa}</strong>
            <span style={{ marginLeft: '0.5rem', fontSize: '10px', opacity: 0.8 }}>
              — zmień uprawnienia poniżej i zapisz
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            <button onClick={cancelEditTemplate}
              style={{ ...styles.btn, border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', fontSize: '11px' }}>
              ✕ Anuluj
            </button>
            <button onClick={saveEditedTemplate}
              style={{ ...styles.btn, backgroundColor: 'var(--success)', color: '#fff', fontSize: '11px' }}>
              💾 Zapisz szablon
            </button>
          </div>
        </div>
      )}

      {/* Lista modułów */}
      {(selectedUser || editingTemplate) && (
        <div>
          {Object.entries(permissionsSchema).map(([moduleKey, moduleData]) => {
            const config = moduleConfig[moduleKey] || { icon: '📋', color: 'var(--text-muted)', name: moduleData.label };
            const status = getModuleStatus(moduleKey);
            const isExpanded = expandedModules[moduleKey];
            const perms = userPermissions[moduleKey] || {};

            return (
              <div key={moduleKey} style={styles.card}>
                {/* Nagłówek modułu - kliknij aby rozwinąć */}
                <div
                  onClick={() => toggleExpand(moduleKey)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    backgroundColor: 'var(--bg)',
                    borderBottom: isExpanded ? '1px solid var(--border)' : 'none'
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{config.icon}</span>
                  <span style={{ fontWeight: '600', fontSize: '12px', flex: 1 }}>{config.name}</span>

                  {/* Status badge */}
                  <span style={{
                    padding: '0.15rem 0.4rem',
                    backgroundColor: status.bg,
                    color: status.color,
                    borderRadius: '3px',
                    fontSize: '9px',
                    fontWeight: '600'
                  }}>
                    {status.label}
                  </span>

                  {/* Quick actions */}
                  <div style={{ display: 'flex', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setModuleLevel(moduleKey, 'full')}
                      title="Pełny dostęp"
                      style={{ ...styles.btn, backgroundColor: status.label === 'Pełny' ? 'var(--success)' : 'var(--border)', color: status.label === 'Pełny' ? '#fff' : 'var(--text-secondary)' }}
                    >
                      ✓✓
                    </button>
                    <button
                      onClick={() => setModuleLevel(moduleKey, 'view')}
                      title="Tylko podgląd"
                      style={{ ...styles.btn, backgroundColor: status.label === 'Podgląd' ? 'var(--info)' : 'var(--border)', color: status.label === 'Podgląd' ? '#fff' : 'var(--text-secondary)' }}
                    >
                      👁
                    </button>
                    <button
                      onClick={() => setModuleLevel(moduleKey, 'none')}
                      title="Brak dostępu"
                      style={{ ...styles.btn, backgroundColor: status.label === 'Brak' ? 'var(--danger)' : 'var(--border)', color: status.label === 'Brak' ? '#fff' : 'var(--text-secondary)' }}
                    >
                      ✕
                    </button>
                  </div>

                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '0.25rem' }}>
                    {isExpanded ? '▲' : '▼'}
                  </span>
                </div>

                {/* Szczegóły uprawnień - rozwijane */}
                {isExpanded && (
                  <div style={{ padding: '0.5rem 0.75rem', backgroundColor: 'var(--card-bg)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.4rem' }}>
                      {Object.entries(moduleData.permissions).map(([permKey, permLabel]) => {
                        const isEnabled = perms[permKey] || false;
                        const isAccessPerm = permKey === 'dostep';

                        return (
                          <div
                            key={permKey}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.35rem 0.5rem',
                              backgroundColor: isEnabled ? 'var(--success-light)' : 'var(--bg)',
                              borderRadius: '4px',
                              border: isAccessPerm ? '1px solid var(--success)' : '1px solid transparent'
                            }}
                          >
                            <Toggle
                              checked={isEnabled}
                              onChange={() => togglePermission(moduleKey, permKey)}
                            />
                            <span style={{
                              fontSize: '10px',
                              color: isEnabled ? 'var(--success)' : 'var(--text-muted)',
                              fontWeight: isAccessPerm ? '600' : '400'
                            }}>
                              {permLabel}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Placeholder gdy brak wyboru */}
      {!selectedUser && selectedLocation && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--bg)', borderRadius: '6px' }}>
          👆 Wybierz użytkownika z listy
        </div>
      )}

      {!selectedLocation && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--bg)', borderRadius: '6px' }}>
          👆 Najpierw wybierz lokalizację
        </div>
      )}

      {/* Mini legenda */}
      <div style={{ marginTop: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--info-light)', borderRadius: '4px', fontSize: '9px', color: 'var(--info)' }}>
        <strong>Szybkie akcje:</strong> ✓✓ = pełny dostęp | 👁 = tylko podgląd | ✕ = brak dostępu.
        Kliknij w moduł aby rozwinąć szczegóły. Przycisk "📋 Szablony" umożliwia zarządzanie szablonami uprawnień.
      </div>
    </div>
  );
};

export default PermissionsTab;
